import React, { useEffect, useState } from 'react';
import { JobPriority, JobType } from '../types/job';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

interface Order {
  id: string;
  order_number: string;
  customer_name: string;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
}

interface CreateJobModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  preSelectedOrderId?: string;
  lockOrder?: boolean;
}

interface FormData {
  order_id: string;
  job_code: string;
  job_type: JobType | null;
  priority: JobPriority;
  planned_start_at: string;
  estimated_duration_hours: string;
  assigned_supervisor_id: string;
  notes: string;
}

const CreateJobModal: React.FC<CreateJobModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
  preSelectedOrderId,
  lockOrder = false,
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [supervisors, setSupervisors] = useState<User[]>([]);
  const [formData, setFormData] = useState<FormData>({
    order_id: preSelectedOrderId || '',
    job_code: '',
    job_type: null,
    priority: JobPriority.NORMAL,
    planned_start_at: '',
    estimated_duration_hours: '',
    assigned_supervisor_id: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (!lockOrder) {
        loadOrders();
      }
      loadSupervisors();
    }
  }, [open, lockOrder]);

  useEffect(() => {
    if (preSelectedOrderId) {
      setFormData(prev => ({ ...prev, order_id: preSelectedOrderId }));
    }
  }, [preSelectedOrderId]);

  const loadOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/orders?status=confirmed&status=in_progress', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setOrders(data.data || []);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
    }
  };

  const loadSupervisors = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use existing users endpoint with role filter
      const response = await fetch('/api/users?role=supervisor&limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const list = Array.isArray(data) ? data : (data.users || data.data || []);
      setSupervisors(list);
    } catch (err) {
      console.error('Error loading supervisors:', err);
    }
  };

  const handleFormChange = (field: keyof FormData, value: string | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value === 'none' ? '' : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const payload: any = {
        order_id: formData.order_id,
        priority: formData.priority,
      };

      if (formData.job_type) payload.job_type = formData.job_type;
      if (formData.job_code.trim()) payload.job_code = formData.job_code.trim();
      if (formData.planned_start_at) payload.planned_start_at = formData.planned_start_at;
      if (formData.estimated_duration_hours) payload.estimated_duration_hours = parseFloat(formData.estimated_duration_hours);
      if (formData.assigned_supervisor_id) payload.assigned_supervisor_id = formData.assigned_supervisor_id;
      if (formData.notes) payload.notes = formData.notes;

      const response = await fetch('/api/jobs/from-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        // Reset form
        setFormData({
          order_id: preSelectedOrderId || '',
          job_code: '',
          job_type: null,
          priority: JobPriority.NORMAL,
          planned_start_at: '',
          estimated_duration_hours: '',
          assigned_supervisor_id: '',
          notes: '',
        });
        onSuccess();
        onOpenChange(false);
      } else {
        setError(data.message || 'Failed to create job');
      }
    } catch (err) {
      setError('An error occurred while creating the job');
      console.error('Error creating job:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create New Job</DialogTitle>
        </DialogHeader>

        {error && (
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="order_id">Order *</Label>
            {lockOrder ? (
              <div className="flex items-center h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm">
                <span className="text-muted-foreground">
                  Order selected from order details
                </span>
              </div>
            ) : (
              <Select
                value={formData.order_id}
                onValueChange={(value) => handleFormChange('order_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an order" />
                </SelectTrigger>
                <SelectContent>
                  {orders.map((order) => (
                    <SelectItem key={order.id} value={order.id}>
                      {order.order_number} - {order.customer_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <Label htmlFor="job_code">Job Code</Label>
            <Input
              id="job_code"
              placeholder="Enter a manual job code (optional)"
              value={formData.job_code}
              onChange={(e) => handleFormChange('job_code', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="job_type">Job Type (Optional)</Label>
              <Select
                value={formData.job_type || 'none'}
                onValueChange={(value) => handleFormChange('job_type', value === 'none' ? null : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Task-based workflow" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Type (Task-Based)</SelectItem>
                  <SelectItem value={JobType.DELIVERY}>Delivery</SelectItem>
                  <SelectItem value={JobType.PICKUP}>Pickup</SelectItem>
                  <SelectItem value={JobType.INSTALLATION}>Installation</SelectItem>
                  <SelectItem value={JobType.MAINTENANCE}>Maintenance</SelectItem>
                  <SelectItem value={JobType.TRANSFER}>Transfer</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Optional. Tasks define the actual work.
              </p>
            </div>

            <div>
              <Label htmlFor="priority">Priority *</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => handleFormChange('priority', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={JobPriority.LOW}>Low</SelectItem>
                  <SelectItem value={JobPriority.NORMAL}>Normal</SelectItem>
                  <SelectItem value={JobPriority.HIGH}>High</SelectItem>
                  <SelectItem value={JobPriority.URGENT}>Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="planned_start_at">Planned Start</Label>
              <Input
                id="planned_start_at"
                type="datetime-local"
                value={formData.planned_start_at}
                onChange={(e) => handleFormChange('planned_start_at', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="estimated_duration_hours">Duration (hours)</Label>
              <Input
                id="estimated_duration_hours"
                type="number"
                step="0.5"
                placeholder="e.g., 8"
                value={formData.estimated_duration_hours}
                onChange={(e) => handleFormChange('estimated_duration_hours', e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="assigned_supervisor_id">Assigned Supervisor (Optional)</Label>
            <Select
              value={formData.assigned_supervisor_id}
              onValueChange={(value) => handleFormChange('assigned_supervisor_id', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select supervisor (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No supervisor assigned</SelectItem>
                {supervisors.map((supervisor) => (
                  <SelectItem key={supervisor.id} value={supervisor.id}>
                    {supervisor.first_name} {supervisor.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              For job oversight. Individual tasks can be assigned to specific users.
            </p>
          </div>

          <div>
            <Label htmlFor="notes">Notes & Instructions</Label>
            <Textarea
              id="notes"
              placeholder="Add any special instructions or notes for this job..."
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !formData.order_id}
            >
              {submitting ? 'Creating...' : 'Create Job'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateJobModal;
